/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { EcsperClientConfig, RequestOptions } from './config.js';
import {
  CONTENT_TYPE_JSON,
  HEADER_AUTHORIZATION,
  HEADER_CLIENT,
  HEADER_CONTENT_TYPE,
  HEADER_REQUEST_ID,
} from './constants.js';

/** Inputs assembled by `buildRequest`, plus the per-request id. */
export interface BuiltRequest {
  readonly request: Request;
  readonly requestId: string;
}

/** Body kinds the client supports. `null` means no body (e.g. GET). */
export type RequestBody =
  | null
  | { readonly kind: 'json'; readonly value: unknown }
  | {
      readonly kind: 'multipart';
      readonly value: FormData;
    };

/**
 * Generate a unique correlation id for a single HTTP request.
 *
 * Stage 5.5 acceptable: native `crypto.randomUUID()` (UUID v4). The programme
 * convention is UUID v7 for stable identifiers; the request id is a
 * short-lived correlation token, not a stable identifier, so v4 is acceptable
 * here per the task specification.
 */
export function generateRequestId(): string {
  return crypto.randomUUID();
}

/** Build the absolute URL for a path, applying any query string parameters. */
export function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string | number | boolean>,
): string {
  const trimmedBase = baseUrl.replace(/\/+$/, '');
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${trimmedBase}${normalisedPath}`);
  if (params !== undefined) {
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.append(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Assemble the headers shared by every request: client id, request id, and
 * (when a token is available) `Authorization`. Caller-supplied headers from
 * `options.headers` override defaults for matching keys.
 */
async function assembleHeaders(
  config: EcsperClientConfig,
  requestId: string,
  body: RequestBody,
  options: RequestOptions | undefined,
): Promise<Headers> {
  const headers = new Headers();
  headers.set(HEADER_REQUEST_ID, requestId);
  headers.set(HEADER_CLIENT, `${config.clientId}/${config.clientVersion}`);

  if (body !== null && body.kind === 'json') {
    headers.set(HEADER_CONTENT_TYPE, CONTENT_TYPE_JSON);
  }

  const token = await config.tokenProvider();
  if (token !== null && token !== '') {
    headers.set(HEADER_AUTHORIZATION, `Bearer ${token}`);
  }

  if (options?.headers !== undefined) {
    for (const [key, value] of Object.entries(options.headers)) {
      headers.set(key, value);
    }
  }

  return headers;
}

/** Convert the supported `RequestBody` kinds into a fetch-compatible body. */
function serialiseBody(body: RequestBody): BodyInit | null {
  if (body === null) return null;
  if (body.kind === 'json') return JSON.stringify(body.value);
  return body.value;
}

/**
 * Build the final `Request` for a single attempt. The supplied
 * `AbortSignal` belongs to the per-attempt timeout / cancellation controller
 * created by the client.
 */
export async function buildRequest(args: {
  readonly config: EcsperClientConfig;
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly body: RequestBody;
  readonly options: RequestOptions | undefined;
  readonly signal: AbortSignal;
  readonly requestId: string;
}): Promise<BuiltRequest> {
  const { config, method, path, body, options, signal, requestId } = args;

  const url = buildUrl(config.baseUrl, path, options?.params);
  const headers = await assembleHeaders(config, requestId, body, options);
  const init: RequestInit = {
    method,
    headers,
    signal,
  };
  const serialised = serialiseBody(body);
  if (serialised !== null) {
    init.body = serialised;
  }
  return { request: new Request(url, init), requestId };
}
